import { useState, useEffect, useCallback, useRef } from "react";
import api from "@/lib/api";

export const PERIOD_OPTIONS = [
  { value: "1d",  label: "1 day",     base: "1m" },
  { value: "2d",  label: "2 days",    base: "1m" },
  { value: "5d",  label: "5 days",    base: "1m" },
  { value: "7d",  label: "7 days",    base: "1m" },
  { value: "10d", label: "10 days",   base: "5m" },
  { value: "30d", label: "30 days",   base: "5m" },
  { value: "60d", label: "60 days",   base: "5m" },
];

export function baseFor(period) { return PERIOD_OPTIONS.find(p => p.value === period)?.base || "1m"; }
export function baseMinutesFor(period) { return baseFor(period) === "5m" ? 5 : 1; }

// localStorage cache so chart paints instantly on next session
const CACHE_PREFIX = "ict.bars.v1.";
const CACHE_TTL_MS = 24 * 60 * 60 * 1000; // 24h
function cacheKey(symbol, period) { return `${CACHE_PREFIX}${symbol}_${period}`; }
function readCache(symbol, period) {
  try {
    const raw = localStorage.getItem(cacheKey(symbol, period));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (Date.now() - (parsed.ts || 0) > CACHE_TTL_MS) return null;
    return parsed;
  } catch { return null; }
}
function writeCache(symbol, period, payload) {
  try {
    localStorage.setItem(cacheKey(symbol, period), JSON.stringify({
      ts: Date.now(),
      main: payload.main, smt: payload.smt, smt_symbol: payload.smt_symbol,
    }));
  } catch {} // quota — silently ignore
}

/**
 * useMarketData — fetches base bars + correlated SMT bars from /api/market/bars.
 * Hydrates instantly from localStorage cache (if fresh) so users never see a blank chart,
 * then fetches latest in background and re-renders when newer data arrives.
 */
export default function useMarketData({ symbol, period, mode, smtOverride, onReset }) {
  const [baseBars, setBaseBars] = useState([]);
  const [baseSmt, setBaseSmt] = useState([]);
  const [smtSymbol, setSmtSymbol] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const liveTimerRef = useRef(null);
  const onResetRef = useRef(onReset);
  useEffect(() => { onResetRef.current = onReset; }, [onReset]);

  const fetchData = useCallback(async () => {
    setError(null);
    // 1) Hydrate from cache for instant paint (no flash of empty chart)
    const cached = readCache(symbol, period);
    if (cached && cached.main?.length && !smtOverride) {
      setBaseBars(cached.main);
      setBaseSmt(cached.smt || []);
      setSmtSymbol(cached.smt_symbol || "");
      onResetRef.current && onResetRef.current(cached.main.length);
    } else {
      setLoading(true);
    }
    // 2) Always refresh in background
    try {
      const interval = baseFor(period);
      const params = { symbol, period, interval };
      if (smtOverride) params.smt_symbol = smtOverride;
      const { data } = await api.get("/market/bars", { params });
      setBaseBars(data.main);
      setBaseSmt(data.smt);
      setSmtSymbol(data.smt_symbol);
      if (!smtOverride) writeCache(symbol, period, data);
      onResetRef.current && onResetRef.current(data.main.length);
    } catch (e) {
      const msg = e.response?.data?.detail || e.message;
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, [symbol, period, smtOverride]);

  useEffect(() => { fetchData(); }, [fetchData]);

  useEffect(() => {
    if (mode !== "live") {
      if (liveTimerRef.current) clearInterval(liveTimerRef.current);
      liveTimerRef.current = null;
      return;
    }
    if (baseFor(period) !== "1m") return;
    const poll = async () => {
      try {
        const { data } = await api.get("/market/live", { params: { symbol } });
        setBaseBars(prev => {
          if (!prev.length) return data.main;
          const lastT = prev[prev.length - 1].time;
          const fresh = data.main.filter(b => b.time > lastT);
          return fresh.length ? [...prev, ...fresh] : prev;
        });
        setBaseSmt(prev => {
          if (!prev.length) return data.smt;
          const lastT = prev[prev.length - 1].time;
          const fresh = data.smt.filter(b => b.time > lastT);
          return fresh.length ? [...prev, ...fresh] : prev;
        });
      } catch {}
    };
    poll();
    liveTimerRef.current = setInterval(poll, 30000);
    return () => { if (liveTimerRef.current) clearInterval(liveTimerRef.current); };
  }, [mode, symbol, period]);

  return { baseBars, baseSmt, smtSymbol, loading, error, refetch: fetchData };
}
